import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtPrice } from "@/lib/api";

export const STATUSES = [
  { key: "estimate", label: "Estimate", dot: "bg-slate-400" },
  { key: "approved", label: "Approved", dot: "bg-sky-400" },
  { key: "in_progress", label: "In progress", dot: "bg-orange-500" },
  { key: "waiting_parts", label: "Waiting parts", dot: "bg-amber-400" },
  { key: "completed", label: "Completed", dot: "bg-emerald-400" },
  { key: "invoiced", label: "Invoiced", dot: "bg-violet-400" },
];

export const VEHICLE_TYPES = [
  { value: "car", label: "Car" },
  { value: "light_truck", label: "Light truck / SUV" },
  { value: "heavy_truck", label: "Heavy-duty truck" },
  { value: "trailer", label: "Trailer" },
  { value: "farm", label: "Farm equipment" },
  { value: "heavy_equipment", label: "Heavy equipment" },
];

export const typeLabel = (t) => VEHICLE_TYPES.find((v) => v.value === t)?.label || t;
export const money = (n) => (n == null ? "—" : fmtPrice(n));
export const vehLabel = (v) => (v ? [v.year, v.make, v.model].filter(Boolean).join(" ") + (v.unit_number ? ` · Unit ${v.unit_number}` : "") : "—");

export const StatusBadge = ({ status }) => {
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 border border-border rounded-sm" data-testid={`status-badge-${status}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}
    </span>
  );
};

export function useApi(path) {
  const [data, setData] = useState(null);
  const load = useCallback(
    () => (path ? api.get(path).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))) : null),
    [path]
  );
  useEffect(() => { load(); }, [load]);
  return [data, load, setData];
}

export const PageHead = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">// {eyebrow}</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold uppercase leading-none">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const inputCls = "w-full h-10 px-3 rounded-sm bg-[#0B0F17] border border-input text-sm focus:outline-none focus:border-primary transition-colors";

export const Select = ({ options, placeholder, className = "", ...props }) => (
  <select className={`${inputCls} ${className}`} {...props}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const FieldInput = ({ f, value, onChange }) => {
  const common = { id: f.name, value: value ?? "", onChange: (e) => onChange(e.target.value), required: f.required, "data-testid": `field-${f.name}` };
  if (f.type === "select") return <Select options={f.options} {...common} />;
  if (f.type === "textarea") return <textarea rows={3} className={`${inputCls} h-auto py-2`} {...common} />;
  return <input type={f.type || "text"} step="any" className={inputCls} {...common} />;
};

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-3xl uppercase">{title}</DialogTitle></DialogHeader>
        {open && <FormBody fields={fields} initial={initial} testid={testid} onSubmit={async (v) => { await onSubmit(v); onOpenChange(false); }} />}
      </DialogContent>
    </Dialog>
  );
}

export function FormBody({ fields, initial, onSubmit, testid }) {
  const [v, setV] = useState(initial || {});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const out = { ...v };
    fields.forEach((f) => { if (f.type === "number") out[f.name] = parseFloat(out[f.name] || 0); else if (out[f.name] === "") out[f.name] = null; });
    try { await onSubmit(out); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <label key={f.name} className={`space-y-1.5 ${f.half ? "" : "col-span-2"}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{f.label}{f.required && " *"}</span>
              <FieldInput f={f} value={v[f.name]} onChange={(val) => setV({ ...v, [f.name]: val })} />
            </label>
          ))}
          <Button type="submit" disabled={busy} className="col-span-2 h-11 rounded-sm font-semibold hover:bg-orange-600 hover:text-white" data-testid={`${testid}-submit`}>
            {busy ? "Saving..." : "Save"}
          </Button>
        </form>
  );
}

export const Stat = ({ label, value, accent, testid }) => (
  <div className="bg-card border border-border rounded-sm p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className={`font-display text-4xl font-bold mt-2 ${accent || ""}`}>{value}</p>
  </div>
);

export const Empty = ({ children, testid }) => (
  <div className="border border-dashed border-border rounded-sm p-10 text-center text-muted-foreground text-sm" data-testid={testid}>{children}</div>
);
